/**
 * @file The filler-facing state of one derived field (SYD-2404 §8, §10): which
 * derive owns it, the sentence explaining where the value came from, when it was
 * last calculated, and whether it is being calculated or is not kept up to date.
 * Consumer: `FieldUI`, which forwards these as primitives to the memoized
 * `FieldLabel`. Every fact is derived at read time; the one effect subscribes to
 * the clock, because a lock going stale is the only transition here that no
 * document write announces.
 * Busy is read from what the server publishes — a lock HOLDER and a queued
 * recompute — never from the lock timestamp alone: that timestamp is the one field
 * `mergeDocumentIntoProxy` refuses to clear on a null write. Only the OFF edge is
 * smoothed, by `FORM_DERIVE_BUSY_TRAIL_MS`, so one edit's burst of short passes reads
 * as one continuous calculation instead of a blinking spinner.
 * Invariants: lockedness is config-derived, but everything HERE is version-derived,
 * so a version whose `formDerives.rev` lags the FormType's `formDeriveRev` yields
 * the absent state — it must not claim anything. The skip REASON never leaves this
 * module; `formDeriveCopy.skippedToFiller` is the only wording a filler sees.
 * Labels come from the shared `useFormDeriveLabelResolvers`, so the sentence in this
 * popover is the sentence the admin list shows, table and column names included.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { FORM_DERIVE_LOCK_STALE_MS, createFormDeriveEngineConfig } from '#types';
import type { FieldId, FormDeriveId } from '#types';
import {
  describeFormDeriveSource,
  formDeriveCopy,
  formDeriveNamesATable,
} from '@/services/formDerives';

import { formStore } from '../store';
import { useFormDeriveLabelResolvers } from './useFormDeriveLabelResolvers';

dayjs.extend(relativeTime);

export type FormDeriveFieldState = {
  /** The derive writing this field, absent when none does or the version is behind. */
  deriveId?: FormDeriveId;
  /** Spec §8's "what it is calculated from", in the admin list's own spelling. */
  sourceSentence?: string;
  /** `Last calculated {relative time}`, absent while this derive has no stamp. */
  computedAtLabel?: string;
  /**
   * A live computation lock on this form version, or a pass queued behind one:
   * every derived field spins at once. True the instant the server says busy, and
   * for `FORM_DERIVE_BUSY_TRAIL_MS` after it stops saying so.
   */
  computing: boolean;
  /** The stamp records a skip: the value is real but frozen. */
  skipped: boolean;
};

/** No derive owns this field, or the version has not caught up with the config. */
const absentFormDeriveFieldState: FormDeriveFieldState = Object.freeze({
  computing: false,
  skipped: false,
});

/**
 * How long `computing` keeps reporting busy after the server's own busy state clears.
 * One cell edit provokes several short passes — the row trigger's, then the verify —
 * each claiming and releasing the lock for about 150ms, which the raw reading renders
 * as a blinking spinner. Trailing only: the hide is always scheduled from a release,
 * which makes this the minimum visible duration too, so nothing else enforces one.
 */
const FORM_DERIVE_BUSY_TRAIL_MS = 500;

/**
 * Millis from a Firestore Timestamp WITHOUT calling a method on it: the value
 * arrives through a Valtio snapshot, and `seconds`/`nanoseconds` are the pair
 * `isTimestampShaped` proves is readable on both SDKs' spellings.
 */
const timestampMillis = (stamp: { seconds: number; nanoseconds: number }): number =>
  stamp.seconds * 1000 + stamp.nanoseconds / 1_000_000;

/**
 * One field's derive state. `fieldId` is a form field: derives target
 * `FormVersion.fields`, so this reads nothing off an entity record.
 */
export const useFormDeriveFieldState = (fieldId: FieldId): FormDeriveFieldState => {
  const snap = useSnapshot(formStore.state);
  const formDerives = snap.formType.data?.formDerives;
  const formDeriveRev = snap.formType.data?.formDeriveRev;

  const config = useMemo(
    () => createFormDeriveEngineConfig({ formDerives, formDeriveRev }),
    [formDerives, formDeriveRev],
  );

  const entry = config.entries.find(
    (candidate) => 'fieldId' in candidate.target && candidate.target.fieldId === fieldId,
  );

  /**
   * Above the guards because a hook cannot be conditional. What it buys is the
   * popover reading "Sum of BOM › Line total" instead of "Sum of bom › lineTotal",
   * so it subscribes to `tableTypes` only for the sources that name a table —
   * every other field, derived or not, opens no listener at all.
   * The reference slot is named by the RECORD in it, never by the slot: a filler
   * knows the customer's name, not the layout's key for the reference.
   */
  const labels = useFormDeriveLabelResolvers(
    (referenceKey) => snap.formVersion.data?.entityReferences?.[referenceKey]?.entityRecordName,
    entry !== undefined && formDeriveNamesATable(entry),
  );

  /**
   * Branch-guarded rather than early-returned, because the clock below is a hook
   * and a hook cannot be conditional. The guard is what carries the subscription
   * discipline `useFormDeriveLiveValue` states: a field no derive targets touches
   * no `formVersion` leaf, so it subscribes to none.
   *
   * Spec §10: staleness against a configuration revision is an administrator's
   * problem and never appears in the form. A never-computed version has no `rev`
   * and is behind by this test, so it reads no lock either.
   */
  const versionState = entry === undefined ? undefined : snap.formVersion.data?.formDerives;
  const versionInSync = versionState !== undefined && versionState.rev === config.rev;

  /**
   * The lock read exactly as the server's own `isFormDeriveLockLive` reads it — a
   * holder AND a timestamp, the holder first. `computingBy` is load-bearing, not
   * corroboration: `mergeDocumentIntoProxy` preserves an existing Timestamp when the
   * incoming value is null, to protect pending `serverTimestamp()` writes, so the
   * release's `computingSince: null` never reaches this proxy while the holder's
   * `null` does. Reading the timestamp alone left the spinner turning against a
   * server that had finished, until a reload rebuilt the slot.
   */
  const heldState = versionInSync && versionState.computingBy ? versionState : undefined;
  const computingSince = heldState?.computingSince;
  const computingSinceMillis = computingSince ? timestampMillis(computingSince) : undefined;

  /**
   * A queued pass, and busy in its own right — the reading `computeAggregations`
   * gives the totals row through `upToDate`. It needs no staleness window: every
   * settled release writes it false, and the one release that leaves it standing
   * summons its own successor through `formDeriveVersionTrigger`.
   */
  const recomputeRequested = versionInSync && versionState.recomputeRequested === true;

  /**
   * The lock this mount has already watched expire. A stale lock is not a
   * document change — nothing releases `computingSince` when an abandoned pass
   * stops — so without a clock the spinner would run until some unrelated render
   * happened to re-read `Date.now()`. Keyed by the lock's own timestamp, so a
   * NEW lock is live again with no effect resetting anything.
   */
  const [expiredLockMillis, setExpiredLockMillis] = useState<number | undefined>(undefined);

  /**
   * One timeout for exactly the window that remains, never a poll: the timer is
   * the external system, the remaining window is what it is being asked. An
   * already-stale lock schedules nothing, because the read below already answers
   * `false`, and a server release lands through the snapshot ahead of any timer.
   */
  useEffect(() => {
    if (computingSinceMillis === undefined) return;
    const remaining = computingSinceMillis + FORM_DERIVE_LOCK_STALE_MS - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => setExpiredLockMillis(computingSinceMillis), remaining);
    return () => clearTimeout(timer);
  }, [computingSinceMillis]);

  /**
   * Busy exactly as the server publishes it, and the raw input to the window below.
   * Both terms already require `versionInSync`, so a field no derive targets and a
   * version behind the configuration read false here without asking again.
   */
  const rawBusy =
    recomputeRequested ||
    (computingSinceMillis !== undefined &&
      expiredLockMillis !== computingSinceMillis &&
      Date.now() - computingSinceMillis < FORM_DERIVE_LOCK_STALE_MS);

  /**
   * Whether the trailing window is still holding the spinner up after the raw state
   * went quiet. It never gates the leading edge — the reading below ORs it with
   * `rawBusy` — so this state can only ever extend a spinner, never delay one.
   */
  const [trailingBusy, setTrailingBusy] = useState(false);

  /**
   * The timer is the external system and the window is what it is being asked. A raw
   * state that goes busy again inside the window cancels the pending hide through
   * this cleanup, which is what merges one edit's burst of passes into one spinner. A
   * field that is not busy — every non-derived field, and every settled one —
   * schedules nothing, and unmounting or losing the derive clears what is pending.
   */
  useEffect(() => {
    if (rawBusy) {
      setTrailingBusy(true);
      return;
    }
    if (!trailingBusy) return;
    const timer = setTimeout(() => setTrailingBusy(false), FORM_DERIVE_BUSY_TRAIL_MS);
    return () => clearTimeout(timer);
  }, [rawBusy, trailingBusy]);

  if (!entry || !versionInSync) return absentFormDeriveFieldState;

  const stamp = versionState.stamps?.[entry.deriveId];

  return {
    deriveId: entry.deriveId,
    sourceSentence: describeFormDeriveSource(entry, labels),
    computedAtLabel: stamp
      ? formDeriveCopy.fieldPopoverStamp(dayjs(timestampMillis(stamp.computedAt)).fromNow())
      : undefined,
    computing: rawBusy || trailingBusy,
    skipped: stamp?.skipped !== undefined,
  };
};
